import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { SocketContext } from "../Socket/SocketContext";
import * as apiVoice from "../../services/apiVoice";
import toastError from "../../errors/toastError";

/**
 * Conversation-scoped Voice state. Mounted INSIDE the Ticket workspace (not
 * the global app tree) so the Call button and the Live Call Bar share one
 * active-call state without touching App.js. When Voice is disabled for the
 * company, `enabled` is false and consumers render nothing.
 */
export const VoiceContext = createContext({});

const ENDED_STATUSES = ["completed", "no_answer", "busy", "failed"];

export const VoiceProvider = ({ children }) => {
  const socketManager = useContext(SocketContext);
  const [status, setStatus] = useState({ enabled: false });
  const [activeCall, setActiveCall] = useState(null);
  // Display identity of the current call's peer (name + avatar), captured at
  // dial time. The provider now lives ABOVE the Ticket (agent-session scope), so
  // the in-call modal can no longer read it from a ticket prop — it reads this.
  const [callPeer, setCallPeer] = useState(null);
  // The just-ended call to drive the post-call Outcome side panel.
  const [outcomeCall, setOutcomeCall] = useState(null);
  // A VISIBLE softphone failure (mic/token/register/connect) — we do NOT auto-
  // fall back to bridge. The UI shows the failure + a manual "phone fallback"
  // button. { message, stage, retry:{toNumber,contactId,ticketId,purpose} }.
  const [softphoneError, setSoftphoneError] = useState(null);
  const activeIdRef = useRef(null);
  // Imperative handle into the browser media layer (SoftphoneProvider, nested
  // inside this provider). Null until that provider mounts and registers — until
  // then "softphone" mode cannot run and falls back to bridge.
  const mediaControllerRef = useRef(null);

  // SoftphoneProvider registers/unregisters its handle here on mount/unmount.
  // Shape: { supported, ensureReady, placeBrowserCall({callId}), mute, hangup }.
  const registerMediaController = useCallback((handle) => {
    mediaControllerRef.current = handle || null;
  }, []);

  // When a call ends, surface it to the Outcome panel exactly once so the agent
  // is led straight into "what happened + next action" (skippable), THEN free
  // the live-call slot so the next call can be placed immediately — without a
  // page refresh. The Outcome panel reads its own `outcomeCall` copy, so
  // clearing `activeCall` here is safe and is what unblocks back-to-back calls.
  useEffect(() => {
    if (activeCall && ENDED_STATUSES.includes(activeCall.status)) {
      setOutcomeCall((prev) => (prev && prev.id === activeCall.id ? prev : activeCall));
      activeIdRef.current = null;
      setActiveCall(null);
      setCallPeer(null);
    }
  }, [activeCall]);

  // One-time feature-gate fetch.
  useEffect(() => {
    let mounted = true;
    apiVoice
      .getVoiceStatus()
      .then((s) => {
        if (mounted) setStatus(s || { enabled: false });
      })
      .catch(() => {
        if (mounted) setStatus({ enabled: false });
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Live call updates via the existing company socket room.
  useEffect(() => {
    if (!status.enabled) return undefined;
    const companyId = localStorage.getItem("companyId");
    if (!companyId || !socketManager || !socketManager.GetSocket) return undefined;
    const socket = socketManager.GetSocket(companyId);
    if (!socket) return undefined;

    const handler = (payload) => {
      const call = payload && payload.call;
      if (!call) return;
      // Only adopt updates for the call this conversation started (outbound).
      if (activeIdRef.current && call.id === activeIdRef.current) {
        setActiveCall(call);
      }
    };

    socket.on(`company-${companyId}-call`, handler);
    return () => {
      socket.off(`company-${companyId}-call`, handler);
    };
  }, [status.enabled, socketManager]);

  const startCall = useCallback(
    async ({ toNumber, contactId, ticketId, purpose, mode, fallbackOf, contactName, contactAvatar }) => {
      const media = mediaControllerRef.current;
      const retry = { toNumber, contactId, ticketId, purpose };
      // Remember who we're calling so the session-level modal shows the name.
      setCallPeer(contactName ? { name: contactName, avatar: contactAvatar } : null);
      // eslint-disable-next-line no-console
      console.log(`🎙️[SP] startCall — mode=${mode} · mediaReady=${!!media} · supported=${media && media.supported}`);

      // Softphone requested but the media layer isn't ready / not supported.
      // NO silent bridge — surface it so the agent knows softphone didn't run.
      if (mode === "softphone" && (!media || media.supported === false)) {
        setSoftphoneError({
          stage: "init",
          message: !media
            ? "מנוע ה-Softphone עדיין נטען — נסה/י שוב בעוד רגע."
            : "הדפדפן/המכשיר לא תומך ב-WebRTC.",
          retry,
        });
        return null;
      }
      setSoftphoneError(null); // a fresh attempt clears any prior failure

      // SOFTPHONE: pre-flight the agent media (mic + token + Device register)
      // BEFORE creating the call, so a mic failure never creates an orphaned
      // queued call or makes the call bar flash and vanish. Only once the agent
      // leg is ready do we create the CallSession.
      if (mode === "softphone") {
        try {
          // eslint-disable-next-line no-console
          console.log("🎙️[SP] pre-flight SIGNALING (token + Device register; mic opens at connect)…");
          if (typeof media.ensureReady === "function") await media.ensureReady();
          // eslint-disable-next-line no-console
          console.log("🎙️[SP] signaling READY ✓ (token+register) — creating CallSession (the mic is acquired by device.connect)");
        } catch (mediaErr) {
          const msg =
            (mediaErr && mediaErr.message) ||
            "Softphone failed: microphone/audio source problem";
          // eslint-disable-next-line no-console
          console.log(`🎙️[SP] ABORTED at stage=${(mediaErr && mediaErr.stage) || "mic"} — ${(mediaErr && mediaErr.name) || ""} — NO bridge, NO server call`);
          setSoftphoneError({
            stage: (mediaErr && mediaErr.stage) || "mic",
            message: msg,
            retry,
          });
          toastError({ response: { data: { message: msg } } });
          return null;
        }
      }

      try {
        const call = await apiVoice.startCall({
          toNumber,
          contactId,
          ticketId,
          purpose,
          mode,
          fallbackOf,
        });
        activeIdRef.current = call.id;
        setActiveCall(call);

        if (mode === "softphone") {
          // Agent media is already up — now place the WebRTC leg. A failure here
          // (connect) is rare; tear the queued call down and surface it (no auto
          // bridge — the banner offers a manual phone fallback).
          try {
            await media.placeBrowserCall({ callId: call.id });
          } catch (mediaErr) {
            try {
              await apiVoice.endCall(call.id);
            } catch (_) {
              /* best-effort cleanup */
            }
            activeIdRef.current = null;
            setActiveCall(null);
            const msg =
              (mediaErr && mediaErr.message) || "חיוג מהדפדפן נכשל בשלב החיבור";
            setSoftphoneError({ stage: (mediaErr && mediaErr.stage) || "connect", message: msg, retry });
            toastError({ response: { data: { message: msg } } });
            return null;
          }
        }

        return call;
      } catch (err) {
        toastError(err);
        return null;
      }
    },
    []
  );

  const clearSoftphoneError = useCallback(() => setSoftphoneError(null), []);

  // Manual fallback: retry the failed softphone attempt as a bridge call.
  const dialBridgeFallback = useCallback(() => {
    if (!softphoneError || !softphoneError.retry) return;
    const retry = softphoneError.retry;
    setSoftphoneError(null);
    startCall({ ...retry, mode: "bridge" });
  }, [softphoneError, startCall]);

  const hangup = useCallback(async () => {
    if (!activeCall) return;
    // For browser calls, kill the local mic/WebRTC leg too; endCall below is the
    // server authority that tears down the customer leg.
    if (activeCall.mode === "softphone" && mediaControllerRef.current) {
      try {
        mediaControllerRef.current.hangup();
      } catch (mediaErr) {
        // Non-fatal: the server endCall still tears the call down.
      }
    }
    try {
      const ended = await apiVoice.endCall(activeCall.id);
      // Optimistically reflect the ended state so the outcome chips appear even
      // if the provider's terminal socket event is missed/dropped. Never clobber
      // an authoritative server terminal (no_answer/busy/failed) with a generic
      // "completed", and always land on a status in ENDED_STATUSES so the
      // Outcome panel auto-opens.
      setActiveCall((prev) => {
        if (!prev || prev.id !== activeCall.id) return prev;
        const serverStatus = ended && ended.status;
        const nextStatus =
          serverStatus && ENDED_STATUSES.includes(serverStatus)
            ? serverStatus
            : ENDED_STATUSES.includes(prev.status)
              ? prev.status
              : "completed";
        return { ...prev, ...(ended || {}), status: nextStatus };
      });
    } catch (err) {
      toastError(err);
    }
  }, [activeCall]);

  // The softphone WebRTC leg terminated (agent hung up, customer hung up, or
  // media dropped). The browser is the only witness — the softphone agent leg
  // has no server status callback — so without this the CallSession stays
  // "active", the call bar timer runs forever, and the customer leg may linger.
  // Tell the server to end the call (authoritative teardown of every leg) and
  // optimistically flip the UI to an ENDED state so the timer stops immediately.
  const handleMediaTerminated = useCallback(async () => {
    const id = activeIdRef.current;
    if (!id) return;
    try {
      const ended = await apiVoice.endCall(id);
      setActiveCall((prev) => {
        if (!prev || prev.id !== id) return prev;
        const serverStatus = ended && ended.status;
        const nextStatus =
          serverStatus && ENDED_STATUSES.includes(serverStatus)
            ? serverStatus
            : ENDED_STATUSES.includes(prev.status)
              ? prev.status
              : "completed";
        return { ...prev, ...(ended || {}), status: nextStatus };
      });
    } catch (err) {
      // Best-effort: the provider's terminal socket event is the backstop.
    }
  }, []);

  const dismissCall = useCallback(() => {
    activeIdRef.current = null;
    setActiveCall(null);
  }, []);

  const closeOutcome = useCallback(() => setOutcomeCall(null), []);
  const openOutcomeFor = useCallback((c) => c && setOutcomeCall(c), []);

  return (
    <VoiceContext.Provider
      value={{
        status,
        activeCall,
        callPeer,
        startCall,
        hangup,
        handleMediaTerminated,
        registerMediaController,
        dismissCall,
        setActiveCall,
        outcomeCall,
        closeOutcome,
        openOutcomeFor,
        softphoneError,
        clearSoftphoneError,
        dialBridgeFallback,
      }}
    >
      {children}
    </VoiceContext.Provider>
  );
};

/**
 * Hard isolation boundary (mirrors SafeSoftphoneProvider): if VoiceProvider ever
 * throws during render, the whole app MUST survive. On failure this renders the
 * children WITHOUT the provider — `useVoice()` then returns the context default
 * `{}`, and every Voice consumer already guards with `if (!status ...) return
 * null`, so all Voice UI silently disappears and the CRM is unaffected. Because
 * the provider now lives at the app-shell level, this boundary is what guarantees
 * "Voice can never break the standard CRM flow."
 */
export class SafeVoiceProvider extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(err) {
    // eslint-disable-next-line no-console
    console.error("[Voice] disabled after a render error (CRM unaffected):", err);
  }

  render() {
    if (this.state.failed) return this.props.children;
    return <VoiceProvider>{this.props.children}</VoiceProvider>;
  }
}

export const useVoice = () => useContext(VoiceContext);
